import { createContext, type Key } from "react";
import { type ITableColumns } from "@domain/models/dms";
import { type ITableDocument } from "@dms/types/Interfaces";
import { type TFilterConfigItem } from "@dms/types/Types";

// Re-export data contexts for dms-internal consumers that haven't migrated yet
export {
    DmsDocumentsContext,
    DmsTypesContext,
    DmsDataContext,
    useDmsDocuments,
    useDmsDocumentsMap,
    useDmsDocument,
    useDmsDocumentsByIds,
    useDmsTypes,
} from "@domain/context/DmsDataContexts";
export type { DmsAssociationsData } from "@domain/context/DmsDataContexts";

interface DmsUserSettingsCtx {
    isTableSize: boolean;
    userConfig: {
        filterConfig: Map<string, TFilterConfigItem>;
        enabledColumns: Map<string, (keyof ITableColumns)[]>;
        columnWidths: Map<string, Map<keyof ITableColumns, number>>;
    };
    setFilterConfig: (typeKey: string[], configData: TFilterConfigItem) => void;
    setTableEnabledColumns: (typeKey: string[], enabledColumns: (keyof ITableColumns)[]) => void;
    setTableColumnWidths: (typeKey: string[], columnWidths: Map<keyof ITableColumns, number>) => void;
    deleteTypeConfig: (typeKey: string[]) => void;
}

/**
 Context for User config state
 */
export const DmsUserSettingsContext = createContext<DmsUserSettingsCtx>({
    isTableSize: false,
    userConfig: { filterConfig: new Map(), enabledColumns: new Map(), columnWidths: new Map() },
    setFilterConfig: () => {},
    setTableEnabledColumns: () => {},
    setTableColumnWidths: () => {},
    deleteTypeConfig: () => {},
});

export type DmsSearchMode = "quick" | "fulltext" | "semantic";

interface IDmsAppCtx {
    activeType: string[];
    view: "list" | "grid";
    isTypeChangeMode: boolean;
    selectedRowKeys: Key[];
    isDraggingRightNow: boolean;
    downloadDocumentsModalOpen: boolean;
    openedMenuItemKeys: string[];
    searchValue: string;
    searchMode: DmsSearchMode;
    /** Server search results (fulltext/semantic). `null` = no server search active. */
    searchResultKeys: ReadonlySet<string> | null;
    folderDocuments: ITableDocument[];
    /**
     * Snapshot of document keys flagged by the duplicate check (same Rechnungsnummer + Betrag
     * inside the current selection). `null` = no duplicate filter active; a non-null set narrows
     * the list/grid to those documents.
     */
    duplicateFilterKeys: ReadonlySet<string> | null;
}

export const DmsAppContext = createContext<IDmsAppCtx>({
    activeType: [],
    view: "list",
    isTypeChangeMode: false,
    selectedRowKeys: [],
    isDraggingRightNow: false,
    downloadDocumentsModalOpen: false,
    openedMenuItemKeys: [],
    searchValue: "",
    searchMode: "quick",
    searchResultKeys: null,
    folderDocuments: [],
    duplicateFilterKeys: null,
});

interface IDmsAppControlCtx {
    selectRow: (key: Key) => void;
    selectRows: (keys: Key[]) => void;
    unselectRow: (keys: Key) => void;
    resetSelectedRows: () => void;
    setActiveType: (type: string[]) => void;
    setViewMode: (arg: IDmsAppCtx["view"]) => void;
    typeChangeModeToggle: (arg: boolean) => void;
    toggleDownloadDocumentsModal: (arg: boolean) => void;
    setOpenedMenuItemKeys: (arg: string[]) => void;
    setSearchValue: (arg: string) => void;
    setSearchMode: (mode: DmsSearchMode) => void;
    setSearchResultKeys: (keys: ReadonlySet<string> | null) => void;
    toggleOpenMenuItem: (arg: string) => void;
    setDuplicateFilterKeys: (keys: ReadonlySet<string> | null) => void;
}

export const DmsAppControlContext = createContext<IDmsAppControlCtx>({
    selectRow: () => {},
    selectRows: () => {},
    unselectRow: () => {},
    resetSelectedRows: () => {},
    setActiveType: () => {},
    toggleDownloadDocumentsModal: () => {},
    setOpenedMenuItemKeys: () => {},
    setSearchValue: () => {},
    setSearchMode: () => {},
    setSearchResultKeys: () => {},
    setViewMode: () => {},
    typeChangeModeToggle: () => {},
    toggleOpenMenuItem: () => {},
    setDuplicateFilterKeys: () => {},
});
